import { useCallback, useRef, useState } from "react";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { WorkflowEdge, WorkflowNode, WorkflowNodeType } from "@/api/content";
import { useLang } from "@/i18n/LanguageContext";
import type { BlockEditorProps } from "./types";

type WfData = { label: string; kind: WorkflowNodeType };
type RFNode = Node<WfData>;

let seq = 0;
const freshId = () => `n${Date.now().toString(36)}${(seq++).toString(36)}`;

export function toRFNodes(nodes: WorkflowNode[]): RFNode[] {
  return nodes.map((n) => ({
    id: n.id,
    position: n.position,
    data: { label: n.label, kind: n.type },
    className: `wf-node wf-node--${n.type}`,
  }));
}
export function toRFEdges(edges: WorkflowEdge[]): Edge[] {
  return edges.map((e) => ({ id: e.id, source: e.source, target: e.target, label: e.label }));
}
function fromRFNodes(nodes: RFNode[]): WorkflowNode[] {
  return nodes.map((n) => ({
    id: n.id,
    type: n.data.kind,
    label: n.data.label,
    position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
  }));
}
function fromRFEdges(edges: Edge[]): WorkflowEdge[] {
  return edges.map((e) => ({ id: e.id, source: e.source, target: e.target, label: typeof e.label === "string" ? e.label : "" }));
}

export function WorkflowEditor({ content, onChange }: BlockEditorProps) {
  const { t } = useLang();
  const [nodes, setNodes] = useState<RFNode[]>(() => toRFNodes(content.nodes ?? []));
  const [edges, setEdges] = useState<Edge[]>(() => toRFEdges(content.edges ?? []));
  const [selected, setSelected] = useState<string | null>(null);

  // Refs hold the latest state so change handlers don't call onChange from inside
  // a setState updater (which React strict mode flags).
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const commit = useCallback(
    (ns: RFNode[], es: Edge[]) => onChange({ nodes: fromRFNodes(ns), edges: fromRFEdges(es) }),
    [onChange],
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const next = applyNodeChanges(changes, nodesRef.current) as RFNode[];
      setNodes(next);
      const removal = changes.some((c) => c.type === "remove");
      if (removal || changes.some((c) => c.type === "position")) {
        let es = edgesRef.current;
        if (removal) {
          const ids = new Set(next.map((n) => n.id));
          es = es.filter((e) => ids.has(e.source) && ids.has(e.target));
          setEdges(es);
        }
        commit(next, es);
      }
    },
    [commit],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const next = applyEdgeChanges(changes, edgesRef.current);
      setEdges(next);
      if (changes.some((c) => c.type === "remove")) commit(nodesRef.current, next);
    },
    [commit],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      const next = addEdge({ ...conn, id: `${conn.source}-${conn.target}-${freshId()}` }, edgesRef.current);
      setEdges(next);
      commit(nodesRef.current, next);
    },
    [commit],
  );

  const defaultLabel = (kind: WorkflowNodeType) =>
    kind === "start" ? t("Start", "Start")
      : kind === "end" ? t("Einde", "End")
        : kind === "decision" ? t("Keuze?", "Decision?")
          : t("Stap", "Step");

  function addNode(kind: WorkflowNodeType) {
    const id = freshId();
    const node: RFNode = {
      id,
      position: { x: 60 + Math.random() * 160, y: 40 + Math.random() * 180 },
      data: { label: defaultLabel(kind), kind },
      className: `wf-node wf-node--${kind}`,
    };
    const next = [...nodesRef.current, node];
    setNodes(next);
    commit(next, edgesRef.current);
    setSelected(id);
  }

  function updateSelected(patch: Partial<WfData>) {
    if (!selected) return;
    const next = nodesRef.current.map((n) =>
      n.id === selected
        ? { ...n, data: { ...n.data, ...patch }, className: `wf-node wf-node--${patch.kind ?? n.data.kind}` }
        : n,
    );
    setNodes(next);
    commit(next, edgesRef.current);
  }

  function deleteSelected() {
    if (!selected) return;
    const next = nodesRef.current.filter((n) => n.id !== selected);
    const ids = new Set(next.map((n) => n.id));
    const es = edgesRef.current.filter((e) => ids.has(e.source) && ids.has(e.target));
    setNodes(next);
    setEdges(es);
    commit(next, es);
    setSelected(null);
  }

  const sel = nodes.find((n) => n.id === selected);
  const TYPE_OPTIONS: { v: WorkflowNodeType; nl: string; en: string }[] = [
    { v: "start", nl: "Start", en: "Start" },
    { v: "step", nl: "Stap", en: "Step" },
    { v: "decision", nl: "Keuze", en: "Decision" },
    { v: "end", nl: "Einde", en: "End" },
  ];

  return (
    <div>
      <div className="wf-toolbar">
        <span className="wf-hint">{t("Sleep knooppunten; sleep van rand naar rand om te verbinden.", "Drag nodes; drag handle to handle to connect.")}</span>
        <div className="spacer" />
        {TYPE_OPTIONS.map((o) => (
          <button key={o.v} type="button" className="btn btn-outline wf-add" onClick={() => addNode(o.v)}>+ {t(o.nl, o.en)}</button>
        ))}
      </div>

      <div className="wf-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(_, n) => setSelected(n.id)}
          onPaneClick={() => setSelected(null)}
          deleteKeyCode={["Delete", "Backspace"]}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      {sel && (
        <div className="wf-panel">
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label>{t("Label", "Label")}</label>
            <input value={sel.data.label} onChange={(e) => updateSelected({ label: e.target.value })} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>{t("Type", "Type")}</label>
            <select value={sel.data.kind} onChange={(e) => updateSelected({ kind: e.target.value as WorkflowNodeType })}>
              {TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{t(o.nl, o.en)}</option>)}
            </select>
          </div>
          <button type="button" className="btn btn-outline" onClick={deleteSelected}>{t("Verwijderen", "Delete")}</button>
        </div>
      )}
    </div>
  );
}
